import { MarginContainer } from "@/components/layout/MarginContainer";
import { type OpenModalProps } from "@/features/calendar/openCalendarModal";
import {
  type WorkHours,
  isWorking,
} from "@/features/calendar/work-schedule/getWorkHours";
import { css, cx } from "@emotion/css";
import { LightMode } from "@repo/skapa";
import { Variables } from "@repo/skapa";
import { Text } from "@repo/skapa";
import { LocalizedWeekday } from "@repo/ui";
import { addMinutes, format, isToday } from "date-fns";

import { DroppableHourBlock } from "./DroppableHourBlock";
import { CalendarColumn, MediumContainer, WeekBlock } from "./components";
import {
  WEEK_BLOCK_HEIGHT,
  WEEK_DATE_TITLE_HEIGHT,
  WEEK_END_HOUR,
} from "./constants";

const DayTitle = ({
  width,
  day,
  isLastDay,
}: {
  width: number;
  day: Date;
  isLastDay: boolean;
}) => (
  <WeekBlock
    height={WEEK_DATE_TITLE_HEIGHT}
    width={width}
    noBorderRight={isLastDay}
  >
    <MarginContainer>
      <div
        className={cx(
          isToday(day) &&
            css`
              border-radius: 50%;
              width: 30px;
              height: 30px;
              padding: 10px;
              background: #fff;
              border: ${Variables.thicknessThick} solid
                ${LightMode.colourSemanticNegative};
              display: flex;
              align-items: center;
              justify-content: center;
            `
        )}
      >
        <Text textStyle="Heading.XS">{format(day, "dd")}</Text>
      </div>
    </MarginContainer>
    <MediumContainer>
      <Text textStyle="Heading.XS">
        <LocalizedWeekday date={day} />
      </Text>
    </MediumContainer>
  </WeekBlock>
);

const DayHourBlocks = ({
  width,
  day,
  startHour,
  hasAllDay,
  allDayHeight,
  openModal,
  workHours,
  isLastDay,
}: {
  width: number;
  day: Date;
  startHour: number;
  hasAllDay: boolean;
  allDayHeight: number;
  isLastDay: boolean;
  workHours: WorkHours;
  openModal: (props: OpenModalProps) => void;
}) => {
  const blocks = [];

  if (hasAllDay) {
    const blockId = `${day.getDay()}-allday`;
    blocks.push(
      <DroppableHourBlock
        key={blockId}
        id={blockId}
        date={day}
        isWorking={true}
        allDaySlot={true}
        height={allDayHeight}
        width={width}
        alignItems="flex-start"
        noBorderRight={isLastDay}
        onClick={() => {
          openModal({
            type: "newTask",
            initialData: {
              startTime: day,
              allDay: true,
            },
          });
        }}
      />
    );
  }

  // Hours 8 - 23
  for (let h = startHour; h <= WEEK_END_HOUR; h++) {
    const date = new Date(day);
    date.setHours(h);
    date.setMinutes(0);

    const blockId = `${day.getDay()}-${h}`;
    blocks.push(
      <DroppableHourBlock
        key={`${blockId}-1`}
        id={`${blockId}-1`}
        date={date}
        allDaySlot={false}
        isWorking={isWorking(workHours, date)}
        noBorderBottom
        height={WEEK_BLOCK_HEIGHT / 2}
        width={width}
        noBorderRight={isLastDay}
        onClick={() => {
          openModal({ type: "newTask", initialData: { startTime: date } });
        }}
      />
    );

    const secondHalfDate = addMinutes(date, 30);
    blocks.push(
      <DroppableHourBlock
        key={`${blockId}-2`}
        id={`${blockId}-2`}
        date={secondHalfDate}
        allDaySlot={false}
        isWorking={isWorking(workHours, secondHalfDate)}
        height={WEEK_BLOCK_HEIGHT / 2}
        width={width}
        noBorderRight={isLastDay}
        noBorderBottom={h === WEEK_END_HOUR}
        onClick={() => {
          openModal({
            type: "newTask",
            initialData: { startTime: secondHalfDate },
          });
        }}
      />
    );
  }
  return <>{blocks}</>;
};

export const CalendarDay = ({
  width,
  day,
  startHour,
  hasAllDay,
  allDayHeight,
  openModal,
  workHours,
  isLastDay,
}: {
  width: number;
  day: Date;
  startHour: number;
  hasAllDay: boolean;
  allDayHeight: number;
  isLastDay: boolean;
  workHours: WorkHours;
  openModal: (props: OpenModalProps) => void;
}) => {
  return (
    <CalendarColumn width={width}>
      <DayTitle width={width} day={day} isLastDay={isLastDay} />
      <DayHourBlocks
        width={width}
        day={day}
        startHour={startHour}
        hasAllDay={hasAllDay}
        allDayHeight={allDayHeight}
        openModal={openModal}
        workHours={workHours}
        isLastDay={isLastDay}
      />
    </CalendarColumn>
  );
};
